import { z } from 'zod';
import { DEFAULT_SHARE_EXPIRY_DAYS, MAX_SHARE_EXPIRY_DAYS } from '../constants.js';
import { shareScopeSchema } from '../enums.js';
/**
 * Création d'un lien de partage (FR-30 à FR-36).
 *
 * L'expiration n'est pas optionnelle : un lien sans date de fin est exactement ce que la
 * plateforme cherche à remplacer.
 */
export const createShareLinkSchema = z.object({
    scope: shareScopeSchema,
    resourceId: z.string().cuid2(),
    expiresInDays: z
        .number()
        .int()
        .min(1)
        .max(MAX_SHARE_EXPIRY_DAYS)
        .default(DEFAULT_SHARE_EXPIRY_DAYS),
    /** Mot de passe optionnel ; stocké haché, jamais renvoyé. */
    password: z.string().min(8).max(200).optional(),
    /** Plafond de consultations, `null` pour illimité dans la limite de l'expiration. */
    maxViews: z.number().int().positive().max(100_000).nullable().default(null),
    /** Le destinataire voit-il les annotations (FR-36). */
    includeAnnotations: z.boolean().default(false),
    /** Le destinataire peut-il télécharger le fichier source. */
    allowDownload: z.boolean().default(false),
    label: z.string().trim().max(120).optional(),
});
export const shareLinkSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    scope: shareScopeSchema,
    resourceId: z.string(),
    /** Jeton public figurant dans l'URL. Renvoyé au créateur uniquement. */
    token: z.string(),
    url: z.string().url(),
    hasPassword: z.boolean(),
    maxViews: z.number().nullable(),
    viewCount: z.number().int().nonnegative(),
    includeAnnotations: z.boolean(),
    allowDownload: z.boolean(),
    label: z.string().nullable(),
    expiresAt: z.coerce.date(),
    revokedAt: z.coerce.date().nullable(),
    createdById: z.string(),
    createdAt: z.coerce.date(),
});
/** Requête d'accès à un lien protégé par mot de passe. */
export const accessShareSchema = z.object({
    password: z.string().max(200).optional(),
});
/**
 * Vue publique d'un partage. Volontairement pauvre : ni identifiants internes d'organisation,
 * ni membres, ni métadonnées non partagées (FR-34).
 */
export const publicShareViewSchema = z.object({
    scope: shareScopeSchema,
    title: z.string(),
    description: z.string().nullable(),
    includeAnnotations: z.boolean(),
    allowDownload: z.boolean(),
    expiresAt: z.coerce.date(),
    videos: z.array(z.object({
        id: z.string(),
        title: z.string(),
        durationSeconds: z.number().nonnegative().nullable(),
        coverUrl: z.string().nullable(),
    })),
    /** Images partagées (scope IMAGE) : URL de lecture pré-signée, courte durée. */
    images: z
        .array(z.object({
        id: z.string(),
        url: z.string().url(),
    }))
        .default([]),
});
/** Entrée du journal d'accès à un lien (FR-35). */
export const shareAccessLogSchema = z.object({
    id: z.string(),
    shareLinkId: z.string(),
    ipAddress: z.string(),
    userAgent: z.string().nullable(),
    accessedAt: z.coerce.date(),
});
//# sourceMappingURL=share.js.map